import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton, InputAdornment, SxProps, Theme } from "@mui/material";
import React from "react";

// Browser detection utility
export const isWebKitSupported = (): boolean => {
    if (typeof document === 'undefined') return false; // SSR safety
    return 'WebkitTextSecurity' in document.createElement('input').style;
};

// Get appropriate input type based on browser capabilities and visibility state
export const getPasswordInputType = (showPassword: boolean): string => {
    const isWebKit = isWebKitSupported();
    
    if (isWebKit) {
        // WebKit browsers: always use 'text' with CSS masking
        return 'text';
    } else {
        // Non-WebKit browsers: use standard password/text toggle
        return showPassword ? 'text' : 'password';
    }
};

export const createErrorAdornment = (hasError: boolean, additionalIcon?: React.ReactNode): React.ReactElement | null => {
    if (!hasError && !additionalIcon) return null;

    return (
        <InputAdornment position="end">
            {hasError && <ErrorOutlineIcon sx={{ color: '#ff2c2c', mr: additionalIcon ? 1 : 0 }} />}
            {additionalIcon}
        </InputAdornment>
    );
};

export const createPasswordVisibilityToggle = (
    showPassword: boolean,
    onToggle: () => void,
    hasValue: boolean
): React.ReactNode => {
    if (!hasValue) return undefined;

    const handleToggle = (event: React.MouseEvent) => {
        event.preventDefault(); // Prevent default button behavior
        event.stopPropagation(); // Stop event bubbling
        
        // Store reference to currently focused element
        const activeElement = document.activeElement as HTMLInputElement;
        const isInputFocused = activeElement && activeElement.tagName === 'INPUT';
        const cursorPosition = isInputFocused ? activeElement.selectionStart : null;
        
        // Toggle password visibility
        onToggle();
        
        // Restore focus and cursor position to maintain keyboard state
        if (isInputFocused) {
            // Use setTimeout to ensure the toggle state change has completed
            setTimeout(() => {
                activeElement.focus();
                if (cursorPosition !== null) {
                    activeElement.setSelectionRange(cursorPosition, cursorPosition);
                }
            }, 0);
        }
    };

    return (
        <IconButton
            aria-label="toggle password visibility"
            onClick={handleToggle}
            onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
            edge="end"
            sx={{ color: '#6c6c6c' }}
            tabIndex={-1} // Remove from tab order to prevent accidental focus
        >
            {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
        </IconButton>
    );
};

// Centralized password field styling utility
export const createPasswordFieldStyles = (showPassword: boolean, baseStyles?: SxProps<Theme>) => {
    const isWebKit = isWebKitSupported();
    
    return {
        ...baseStyles,
        '& .MuiOutlinedInput-input': {
            fontFamily: 'Raleway, sans-serif', // Always use Raleway font
            // Only apply WebkitTextSecurity in WebKit browsers
            ...(isWebKit && {
                WebkitTextSecurity: showPassword ? 'none' : 'disc',
            }),
            // Ensure placeholder uses Raleway font on all browsers, especially Safari
            '&::placeholder': {
                fontFamily: 'Raleway, sans-serif',
            },
            '&::-webkit-input-placeholder': {
                fontFamily: 'Raleway, sans-serif',
            },
            '&::-moz-placeholder': {
                fontFamily: 'Raleway, sans-serif',
            },
            '&:-ms-input-placeholder': {
                fontFamily: 'Raleway, sans-serif',
            },
        }
    };
}; 